import {Naja} from 'naja';
import IExtension from '../../extensions/IExtension';

export const TranslationStatus = {
    Translated: 'translated',
    Unchanged: 'unchanged',
    Unavailable: 'unavailable',
    Error: 'error',
} as const;
export type TranslationStatus = typeof TranslationStatus[keyof typeof TranslationStatus];

export type TranslationReference = {type: string, id: string};
export type TranslationResult = TranslationReference & {
    status: TranslationStatus;
    fields?: {[name: string]: string};
};

type Subscriber = (result: TranslationResult) => void;
type PendingItem = {
    reference: TranslationReference;
    individual: boolean;
    subscribers: Set<Subscriber>;
};

export class ContentTranslationClient implements IExtension {
    private naja: Naja;
    private url: string | null = null;
    private readonly pending = new Map<string, PendingItem>();
    private timer: number | null = null;

    public initialize(naja: Naja): void {
        this.naja = naja;
        this.url = document.querySelector<HTMLMetaElement>('meta[name="content-translation-url"]')?.content || null;
        // Background requests must not reset another form's loaders or submit protection.
        // This extension is registered before the application's UI extensions.
        (['before', 'success', 'error', 'complete'] as const).forEach(type => {
            naja.addEventListener(type, event => {
                if (event.detail.options.contentTranslation) {
                    event.stopImmediatePropagation();
                }
            });
        });
    }

    public subscribe(reference: TranslationReference, subscriber: Subscriber, individual = false): () => void {
        const key = JSON.stringify([reference.type, reference.id]);
        let item = this.pending.get(key);

        // A newly rendered block must not receive a response for its previous render.
        if (!item) {
            item = {reference, individual, subscribers: new Set()};
            this.pending.set(key, item);
        }

        item.individual ||= individual;
        item.subscribers.add(subscriber);

        this.schedule();

        return () => {
            item.subscribers.delete(subscriber);
            if (item.subscribers.size === 0 && this.pending.get(key) === item) {
                this.pending.delete(key);
            }
        };
    }

    public request(reference: TranslationReference, individual = false): Promise<TranslationResult> {
        return new Promise(resolve => {
            const unsubscribe = this.subscribe(reference, result => {
                unsubscribe();
                resolve(result);
            }, individual);
        });
    }

    private schedule(): void {
        if (this.timer !== null) {
            return;
        }
        this.timer = window.setTimeout(() => {
            this.timer = null;
            this.sendNext();
        }, 20);
    }

    private sendNext(): void {
        const batch = Array.from(this.pending.entries()).slice(0, 20);
        if (batch.length === 0) {
            return;
        }
        batch.forEach(([key]) => this.pending.delete(key));
        const items = batch.map(([, item]) => item);
        items.filter(item => item.individual).forEach(item => { void this.send([item]); });
        const textItems = items.filter(item => !item.individual);
        if (textItems.length > 0) {
            void this.send(textItems);
        }
        if (this.pending.size > 0) {
            this.schedule();
        }
    }

    private async send(batch: PendingItem[]): Promise<void> {
        try {
            if (!this.url) {
                throw new Error('Content translation URL is unavailable.');
            }
            const payload = await this.naja.makeRequest(
                'POST',
                this.url,
                JSON.stringify({items: batch.map(item => item.reference)}),
                {
                    history: false,
                    abort: false,
                    unique: false,
                    contentTranslation: true,
                    fetch: {headers: {'Content-Type': 'application/json'}},
                },
            );
            const results = Array.isArray(payload?.items) ? payload.items : [];
            batch.forEach(item => {
                const result = results.find((candidate: TranslationResult) =>
                    candidate?.type === item.reference.type && candidate?.id === item.reference.id,
                );
                this.notify(item, result || {...item.reference, status: TranslationStatus.Error});
            });
        } catch (error) {
            batch.forEach(item => this.notify(item, {...item.reference, status: TranslationStatus.Error}));
        }
    }

    private notify(item: PendingItem, result: TranslationResult): void {
        item.subscribers.forEach(subscriber => subscriber(result));
    }
}

export default new ContentTranslationClient();
